import { fmt } from "@/lib/api";
import { Gem, History, Crown, ArrowUpRight, User } from "lucide-react";

const roleStyle = {
  creator: { icon: Crown, dot: "text-amber-300" },
  upline: { icon: ArrowUpRight, dot: "text-cyan-300" },
  holder: { icon: User, dot: "text-emerald-300" },
};

export function SettlementCard({ card, index, testid = "silver-settlement" }) {
  const recips = card.settlement?.recipients || [];
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`${testid}-${index}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
            <Gem className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold text-white">Silver Card #{index + 1} completed</div>
            <div className="text-xs text-slate-500">
              {card.completed_at ? new Date(card.completed_at).toLocaleString() : "—"}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Total distributed</div>
          <div className="font-num text-emerald-300 font-bold" data-testid={`${testid}-total-${index}`}>
            {fmt(card.payout || card.settlement?.total || 0)}
          </div>
        </div>
      </div>

      {recips.length === 0 ? (
        <p className="text-xs text-slate-500">Breakdown not recorded for this card (completed before settlement tracking was added).</p>
      ) : (
        <div className="space-y-1.5">
          {recips.map((r, i) => {
            const st = roleStyle[r.role] || roleStyle.upline;
            const Icon = st.icon;
            const lapsed = (r.lapsed || 0) > 0;
            return (
              <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 bg-white/[0.015]" data-testid={`${testid}-recipient-${index}-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className={`h-4 w-4 shrink-0 ${st.dot}`} />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">
                      {r.label}{r.role === "upline" ? ` · Level ${r.level}` : ""}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {r.name}{r.referral_code ? ` · ${r.referral_code}` : ""}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 pl-3">
                  <div className="font-num text-white text-sm">{fmt(r.amount)}</div>
                  {lapsed && (
                    <div className="text-[0.65rem] text-amber-400">
                      {r.credited > 0 ? `${fmt(r.credited)} credited · ${fmt(r.lapsed)} lapsed` : "lapsed to Creator"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SettlementHistory({ cards, total = 27, testid = "silver-settlement" }) {
  const settled = (cards || []).filter((c) => c.status === "completed" || c.settled);
  return (
    <div className="glass rounded-2xl p-6" data-testid={`${testid}-history`}>
      <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-5">
        <History className="h-5 w-5 text-cyan-400" /> Settlement History
      </h3>
      {settled.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center" data-testid={`${testid}-empty`}>
          No completed Silver Cards yet. The settlement breakdown appears here once a card's Level-3 matrix fills.
        </p>
      ) : (
        <div className="space-y-4">
          {settled.map((c, i) => <SettlementCard key={c.id} card={c} index={i} testid={testid} />)}
        </div>
      )}
    </div>
  );
}
